import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Chip,
  Avatar,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import {
  FindInPage as FindIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";
import { useLostItems, useItemStatistics } from "../../hooks/useApi";

const HomePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { data } = useLostItems(useMemo(() => ({ limit: 5 }), []));
  const recentItems = data?.items || [];
  const totalItems = data?.total_items || 0;

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "おはようございます";
    if (hour < 18) return "こんにちは";
    return "こんばんは";
  };

  const quickActions = [
    {
      title: "落とし物を検索",
      description: "見つかった落とし物を検索できます",
      icon: <FindIcon />,
      color: theme.palette.primary.main,
      action: () => navigate("/items"),
      roles: [],
    },
    {
      title: "新規登録",
      description: "新しい落とし物を登録します",
      icon: <AddIcon />,
      color: theme.palette.success.main,
      action: () => navigate("/register"),
      roles: ["staff", "admin"],
    },
    {
      title: "ダッシュボード",
      description: "統計情報を確認できます",
      icon: <DashboardIcon />,
      color: theme.palette.secondary.main,
      action: () => navigate("/dashboard"),
      roles: ["staff", "admin"],
    },
  ];

  const { data: statsData } = useItemStatistics();
  // const recentItems = data?.items || []; // useLostItems is already called above, but we need to be careful not to conflict if we were to use statsData for everything.
  // Actually, the original code used `data` from `useLostItems` for `recentItems` and `totalItems`.
  // But `totalItems` is also available in `statsData`.
  // Let's keep `recentItems` from `useLostItems` as it gives the actual items list.
  // We will use `statsData` for the stats cards.

  const stats = [
    {
      title: "総アイテム数",
      value: statsData?.total_items || totalItems || 0,
      icon: <AssignmentIcon />,
      color: theme.palette.info.main,
    },
    {
      title: "アクティブユーザー",
      value: statsData?.active_users || 0,
      icon: <PeopleIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "今月の登録数",
      value: statsData?.recent_items || 0,
      icon: <TrendingUpIcon />,
      color: theme.palette.warning.main,
    },
  ];

  const isActionVisible = (roles: string[]) => {
    if (roles.length === 0) return true;
    if (!userProfile) return false;
    return roles.some((role) => userProfile.roles.includes(role));
  };

  if (!currentUser) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url('/images/hero-bg.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&::before": {
            content: "''",
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
          },
        }}
      >
        <Card
          sx={{
            position: "relative",
            p: 4,
            maxWidth: 480,
            textAlign: "center",
            background: "rgba(255,255,255,0.12)",
            backdropFilter: "blur(12px)",
            borderRadius: 2,
            boxShadow: 4,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: "common.white", fontWeight: 700 }}
          >
            🔮 Arcana Lost &amp; Found
          </Typography>
          <Typography variant="h6" color="common.white" paragraph>
            先進的な落とし物管理システムへようこそ。AI技術で効率的に管理できます。
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/login")}
            sx={{
              mt: 2,
              px: 4,
              py: 1.5,
              fontSize: "1rem",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            ログイン
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            {getWelcomeMessage()}、
            {currentUser.displayName || currentUser.email}さん
          </Typography>
          <Typography variant="body1" color="text.secondary">
            今日も素晴らしい一日をお過ごしください。システムの最新情報をご確認ください。
          </Typography>
        </Box>

        {/* User Info */}
        <Card
          sx={{
            mb: 4,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={currentUser.photoURL || ""}
                alt={currentUser.displayName || ""}
                sx={{ width: 64, height: 64 }}
              >
                {currentUser.email?.[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">
                  {currentUser.displayName || currentUser.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {currentUser.email}
                </Typography>
                {userProfile && (
                  <Stack direction="row" spacing={1}>
                    {userProfile.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        color={
                          role === "admin"
                            ? "error"
                            : role === "staff"
                            ? "warning"
                            : "default"
                        }
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Stats */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 3,
            mb: 4,
          }}
        >
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{ fontWeight: 700 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      backgroundColor: alpha(stat.color, 0.1),
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Quick Actions */}
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 600, mb: 3 }}
        >
          クイックアクション
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 3,
            mb: 4,
          }}
        >
          {quickActions
            .filter((action) => isActionVisible(action.roles))
            .map((action, index) => (
              <Card
                key={index}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[8],
                  },
                }}
                onClick={action.action}
              >
                <CardContent>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: alpha(action.color, 0.1),
                        color: action.color,
                      }}
                    >
                      {action.icon}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="div" gutterBottom>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
        </Box>

        {/* Recent Items */}
        {recentItems.length > 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                最近の落とし物
              </Typography>
              <Button variant="outlined" onClick={() => navigate("/items")}>
                すべて見る
              </Button>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              {recentItems.slice(0, 4).map((item) => (
                <Card
                  key={item.id}
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => navigate(`/items/${item.id}`)}
                >
                  {item.media_urls && item.media_urls[0] && (
                    <Box
                      component="img"
                      sx={{
                        height: 140,
                        width: "100%",
                        objectFit: "cover",
                      }}
                      src={item.media_urls[0]}
                      alt={item.name}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="div"
                      noWrap
                    >
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {item.found_location}
                    </Typography>
                    <Chip
                      label={item.status}
                      size="small"
                      color={item.status === "Found" ? "success" : "default"}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default HomePage;
